import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModalStore } from '../../store/modalStore';
import { useToastStore } from '../../store/toastStore';
import { useAuthStore } from '../../store/authStore';
import type { Book } from '../../api/books';

interface DeleteImpact {
  bookmarks: number;
  reading_lists: number;
  chunks: number;
}

export function DeleteBookModal() {
  const navigate = useNavigate();
  const { isOpen, modalType, modalData, closeModal } = useModalStore();
  const addToast = useToastStore((state) => state.addToast);
  const accessToken = useAuthStore((state) => state.accessToken);

  const [deleting, setDeleting] = useState(false);
  const [impact, setImpact] = useState<DeleteImpact>({
    bookmarks: 0,
    reading_lists: 0,
    chunks: 0,
  });

  const book = modalData as Book;

  useEffect(() => {
    const fetchImpact = async () => {
      if (isOpen && modalType === 'deleteBook' && book) {
        try {
          const headers: Record<string, string> = {};
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
          }

          const response = await fetch(`/api/books/${book.id}/delete-impact`, {
            credentials: 'include',
            headers,
          });

          if (response.ok) {
            const data = await response.json();
            setImpact(data);
          }
        } catch (err) {
          console.error('Failed to fetch delete impact:', err);
        }
      }
    };

    fetchImpact();
  }, [isOpen, modalType, book, accessToken]);

  if (!isOpen || modalType !== 'deleteBook' || !book) return null;

  const handleDelete = async () => {
    setDeleting(true);

    try {
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`/api/books/${book.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete book');
      }

      addToast('Book deleted successfully', 'success');
      closeModal();
      setTimeout(() => navigate('/books'), 500);
    } catch (err) {
      addToast((err as Error).message, 'error');
      setDeleting(false);
    }
  };

  const hasImpact = impact.bookmarks > 0 || impact.reading_lists > 0 || impact.chunks > 0;

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Delete Book</h2>
          <button onClick={closeModal} className="btn-close" type="button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to delete <strong>{book.title}</strong>?
          </p>

          {hasImpact && (
            <div className="delete-impact">
              <p><strong>This will also delete:</strong></p>
              <ul>
                {impact.bookmarks > 0 && <li>{impact.bookmarks} bookmark(s)</li>}
                {impact.reading_lists > 0 && <li>{impact.reading_lists} reading list(s)</li>}
                {impact.chunks > 0 && <li>{impact.chunks} content chunk(s)</li>}
              </ul>
            </div>
          )}

          <p className="warning">This action cannot be undone.</p>
        </div>

        <div className="modal-footer">
          <button onClick={closeModal} className="btn btn-secondary" type="button">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-danger"
            disabled={deleting}
            type="button"
          >
            {deleting ? 'Deleting...' : 'Delete Book'}
          </button>
        </div>
      </div>
    </div>
  );
}
